import type { NavEntry, NavGroup } from './types';

export const NAVIGATION_REGISTRY: NavEntry[] = [
  {
    id: 'home',
    title: 'Home',
    group: 'CORE',
    href: '/',
    iconName: 'HomeIcon',
    aliases: ['dashboard', 'main'],
    keywords: ['start', 'landing'],
    description: 'Dashboard and quick links',
    launcher: true,
  },
  {
    id: 'scheduling',
    title: 'Scheduling',
    group: 'CORE',
    href: '/scheduling',
    iconName: 'CalendarDaysIcon',
    aliases: ['schedule builder', 'timetable', 'classes'],
    keywords: ['generate', 'conflicts', 'calendar'],
    description: 'Build and compare course schedules',
    launcher: true,
  },
  {
    id: 'course-sniper',
    title: 'Course Sniper',
    group: 'SCHEDULING TOOLS',
    href: '/scheduling/alerts',
    iconName: 'TargetIcon',
    aliases: ['section alerts', 'snipe', 'open sections', 'registration'],
    keywords: ['notify', 'watch'],
    description: 'Track course section availability',
    launcher: true,
  },
  {
    id: 'explore',
    title: 'Explore',
    group: 'SCHEDULING TOOLS',
    href: '/explore',
    iconName: 'MagnifyingGlassIcon',
    aliases: ['course search', 'catalog', 'browse courses'],
    keywords: ['find classes', 'ratings', 'sirs'],
    description: 'Search and filter the course catalog',
    launcher: true,
  },
  {
    id: 'plan',
    title: 'Plan',
    group: 'ACADEMIC PLANNING',
    href: '/planning',
    iconName: 'AcademicCapIcon',
    aliases: ['academic planning', 'degree plan', 'requirements', 'gpa planner'],
    keywords: ['roadmap', 'prerequisites', 'academic record', 'graduation'],
    description: 'Plan degree progress, requirements, records, and GPA scenarios',
    launcher: true,
  },
  {
    id: 'campus',
    title: 'Campus',
    group: 'CAMPUS',
    href: '/campus',
    iconName: 'MapPinIcon',
    aliases: ['campus hub', 'map', 'parking', 'buses', 'dining', 'events'],
    keywords: ['places', 'transportation', 'campus life'],
    description: 'Open the campus map and live service handoffs',
    launcher: true,
  },
  {
    id: 'ai-assistant',
    title: 'Ask Scarlet Sync',
    group: 'CORE',
    href: '/ai-assistant',
    iconName: 'ChatBubbleLeftRightIcon',
    aliases: ['ai assistant', 'scarlet', 'chat', 'ask ai'],
    keywords: ['help', 'advisor'],
    description: 'Ask for help across schedules, courses, and degree planning',
    launcher: true,
  },
  {
    id: 'course-wiki',
    title: 'Course Wiki',
    group: 'CORE',
    href: '/courses',
    iconName: 'BookOpenIcon',
    aliases: ['wiki', 'course wiki', 'course guides', 'course profiles'],
    keywords: ['research', 'syllabus', 'study guide', 'course notes'],
    description: 'Open researched course guides and catalog profiles',
    hubId: 'explore',
  },
  {
    id: 'prerequisite-checker',
    title: 'Prerequisite Checker',
    group: 'ACADEMIC PLANNING',
    href: '/prerequisite-checker',
    iconName: 'ClipboardDocumentCheckIcon',
    aliases: ['prereqs', 'requirements check'],
    keywords: ['eligibility'],
    description: 'Validate course prerequisites',
    hubId: 'plan',
  },
  {
    id: 'gpa-calculator',
    title: 'GPA Planner',
    group: 'ACADEMIC PLANNING',
    href: '/gpa-calculator',
    iconName: 'CalculatorIcon',
    aliases: ['gpa calculator', 'target gpa', 'grade calculator'],
    keywords: ['semester gpa', 'scholarship renewal', 'deans list', 'academic standing'],
    description: 'Project GPA and review academic milestones',
    hubId: 'plan',
  },
  {
    id: 'degree-planning',
    title: 'Degree Planning',
    group: 'ACADEMIC PLANNING',
    href: '/degree-planning',
    iconName: 'AcademicCapIcon',
    aliases: ['degree planner', 'graduation plan', 'four year plan'],
    keywords: ['requirements', 'credits', 'semester'],
    description: 'Track degree progress over semesters',
    hubId: 'plan',
  },
  {
    id: 'degree-search',
    title: 'Degree Search',
    group: 'ACADEMIC PLANNING',
    href: '/degree-search',
    iconName: 'DocumentMagnifyingGlassIcon',
    aliases: ['majors', 'minors', 'programs'],
    keywords: ['degree catalog'],
    description: 'Browse Rutgers degree programs',
    hubId: 'plan',
  },
  {
    id: 'course-lists',
    title: 'Course Lists',
    group: 'ACADEMIC PLANNING',
    href: '/course-lists',
    iconName: 'BookOpenIcon',
    aliases: ['saved lists', 'collections'],
    keywords: ['curated courses'],
    description: 'Official and custom course collections',
    hubId: 'plan',
  },
  {
    id: 'sirs',
    title: 'SIRS Ratings',
    group: 'ACADEMIC PLANNING',
    href: '/sirs',
    iconName: 'StarIcon',
    aliases: ['evaluations', 'professor ratings', 'course quality'],
    keywords: ['teaching', 'reviews'],
    description: 'Student Instructional Rating Statistics',
    hubId: 'plan',
  },
  {
    id: 'research-opportunities',
    title: 'Research Opportunities',
    group: 'RESEARCH',
    href: '/research-opportunities',
    iconName: 'BeakerIcon',
    aliases: ['reu', 'nsf', 'labs'],
    keywords: ['undergraduate research'],
    description: 'Find research projects and REU sites',
  },
  {
    id: 'research-outreach',
    title: 'Research Outreach',
    group: 'RESEARCH',
    href: '/research-outreach',
    iconName: 'MegaphoneIcon',
    aliases: ['cold email', 'professor email'],
    keywords: ['outreach drafts'],
    description: 'Draft research outreach emails',
  },
  {
    id: 'ontology',
    title: 'Ontology',
    group: 'RESEARCH',
    href: '/ontology',
    iconName: 'CircleStackIcon',
    aliases: ['knowledge graph'],
    keywords: ['entities', 'relationships'],
    description: 'Explore the course knowledge graph',
  },
  {
    id: 'events',
    title: 'Events',
    group: 'CAMPUS',
    href: '/events',
    iconName: 'TicketIcon',
    aliases: ['campus events', 'activities'],
    keywords: ['tonight', 'free', 'sports'],
    description: 'Campus events and activities',
    hubId: 'campus',
  },
  {
    id: 'parking-map',
    title: 'Parking',
    group: 'CAMPUS',
    href: '/parking-map',
    iconName: 'MapPinIcon',
    aliases: ['permits', 'lots'],
    keywords: ['parking map'],
    description: 'Parking permits and lot map',
    hubId: 'campus',
  },
  {
    id: 'bus-tracker',
    title: 'Bus Tracker',
    group: 'CAMPUS',
    href: '/bus-tracker',
    iconName: 'TruckIcon',
    aliases: ['rutgers bus', 'passio', 'transit'],
    keywords: ['eta', 'routes'],
    description: 'Real-time Rutgers bus locations',
    hubId: 'campus',
  },
  {
    id: 'dining',
    title: 'Dining',
    group: 'CAMPUS',
    href: '/dining',
    iconName: 'DiningIcon',
    aliases: ['menus', 'food', 'meal plan'],
    keywords: ['hours', 'nutrition'],
    description: 'Dining hall menus and hours',
    hubId: 'campus',
  },
  {
    id: 'class-chat',
    title: 'Class Chat',
    group: 'CAMPUS',
    href: '/class-chat',
    iconName: 'ChatBubbleLeftRightIcon',
    aliases: ['messaging', 'classroom chat'],
    keywords: ['messages'],
    description: 'Real-time class messaging',
    hubId: 'campus',
  },
  {
    id: 'room-availability',
    title: 'Room Availability',
    group: 'CAMPUS',
    href: '/room-availability',
    iconName: 'BuildingOffice2Icon',
    aliases: ['study rooms', 'empty rooms'],
    keywords: ['availability', 'booking'],
    description: 'Find available study rooms',
    hubId: 'campus',
  },
  {
    id: 'classroom-guide',
    title: 'Classroom Guide',
    group: 'CAMPUS',
    href: '/rooms',
    iconName: 'BuildingOffice2Icon',
    aliases: ['room details', 'classroom finder', 'room photos'],
    keywords: ['capacity', 'seating', 'technology', 'panorama', 'accessibility'],
    description: 'Preview Rutgers classrooms and equipment',
    hubId: 'campus',
  },
  {
    id: 'info-rutgers',
    title: 'Info Rutgers',
    group: 'CAMPUS',
    href: '/info-rutgers',
    iconName: 'InformationCircleIcon',
    aliases: ['rutgers qa', 'campus info'],
    keywords: ['questions', 'grounded search'],
    description: 'Grounded Rutgers Q&A chatbot',
    hubId: 'campus',
  },
  {
    id: 'profile',
    title: 'Profile',
    group: 'PROFILE',
    href: '/profile',
    iconName: 'UserIcon',
    aliases: ['account', 'my profile'],
    keywords: ['settings', 'user'],
    description: 'Account and saved data',
  },
  {
    id: 'optimal-setup',
    title: 'Optimal Setup',
    group: 'PROFILE',
    href: '/optimal-setup',
    iconName: 'SparklesIcon',
    aliases: ['onboarding', 'permissions', 'notifications', 'widgets'],
    keywords: ['calendar', 'commuter', 'course sniper sms', 'location'],
    description: 'Optional device setup and permission previews',
  },
  {
    id: 'friends',
    title: 'Friends',
    group: 'PROFILE',
    href: '/friends',
    iconName: 'UserGroupIcon',
    aliases: ['social', 'connections'],
    keywords: ['share schedules'],
    description: 'Friend discovery and shared schedules',
  },
  {
    id: 'help',
    title: 'Help',
    group: 'PROFILE',
    href: '/help',
    iconName: 'QuestionMarkCircleIcon',
    aliases: ['help center', 'support hub', 'guides and resources'],
    keywords: ['support', 'guides', 'resources', 'feedback'],
    description: 'Find support, practical guides, and Rutgers resources',
  },
  {
    id: 'support',
    title: 'Support',
    group: 'PROFILE',
    href: '/support',
    iconName: 'QuestionMarkCircleIcon',
    aliases: ['help', 'contact'],
    keywords: ['feedback', 'bug'],
    description: 'Get help and send feedback',
  },
  {
    id: 'rutgers-planning-guides',
    title: 'Rutgers Planning Guides',
    group: 'PROFILE',
    href: '/guides',
    iconName: 'BookOpenIcon',
    aliases: ['webreg guide', 'transfer guide', 'commuter guide', 'schedule help'],
    keywords: ['new brunswick', 'registration', 'planning'],
    description: 'Practical Rutgers scheduling, transfer, and commuter guides',
  },
  {
    id: 'design-system',
    title: 'Design System',
    group: 'PROFILE',
    href: '/design-system',
    iconName: 'SparklesIcon',
    aliases: ['design audit', 'ui system', 'component catalog'],
    keywords: ['colors', 'buttons', 'cards', 'badges', 'patterns'],
    description: 'UI elements and design consistency audit',
  },
  {
    id: 'profile-previous-classes',
    title: 'Previous Classes',
    group: 'PROFILE',
    href: '/profile?tab=previous_classes',
    iconName: 'AcademicCapIcon',
    aliases: ['completed courses', 'transcript', 'history'],
    keywords: ['taken classes'],
    description: 'Courses you have already completed',
  },
  {
    id: 'profile-memories',
    title: 'AI Memories',
    group: 'PROFILE',
    href: '/profile?tab=memories',
    iconName: 'LightBulbIcon',
    aliases: ['memory', 'assistant memory'],
    keywords: ['ai context'],
    description: 'Memories Ask Scarlet Sync has saved about you',
  },
  {
    id: 'profile-schedules',
    title: 'Saved Schedules',
    group: 'PROFILE',
    href: '/profile?tab=schedules',
    iconName: 'CalendarDaysIcon',
    aliases: ['my schedules'],
    keywords: ['saved timetables'],
    description: 'Your saved schedule plans',
  },
  {
    id: 'profile-course-lists',
    title: 'My Course Lists',
    group: 'PROFILE',
    href: '/profile?tab=courses',
    iconName: 'BookOpenIcon',
    aliases: ['saved lists'],
    keywords: ['collections'],
    description: 'Course lists you created',
  },
  {
    id: 'profile-degree-plans',
    title: 'Degree Plans',
    group: 'PROFILE',
    href: '/profile?tab=degree_plans',
    iconName: 'DocumentTextIcon',
    aliases: ['my degree plans', 'saved plans'],
    keywords: ['progress'],
    description: 'Your saved degree plans',
  },
];

export const NAV_SECTION_ORDER: NavGroup[] = [
  'CORE',
  'SCHEDULING TOOLS',
  'ACADEMIC PLANNING',
  'RESEARCH',
  'CAMPUS',
  'PROFILE',
];

export function getNavSections() {
  return NAV_SECTION_ORDER.map((title) => ({
    title,
    items: NAVIGATION_REGISTRY.filter((entry) => entry.group === title),
  })).filter((section) => section.items.length > 0);
}
